import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Alert,
  Checkbox,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon from "@mui/icons-material/Close";
import type { Theme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  CompanyClientResponse,
  MonthResponses,
  OrderCartPayload,
  OrderJsonResponse,
  Product,
  ProductType,
} from "../../types/api.types";
import { orderService } from "../../services/order/orderService";
import { productService } from "../../services/product/productService";
import DataTable from "../../components/DataTable";
import { COMPANY_CHANGED_EVENT } from "../../utils/constants";
import { StockErrorDialog } from "../../shared/StockErrorDialog";
import { Controller, useForm } from "react-hook-form";

// interface MonthData {
//   mes: string;
//   observacion: string;
//   cantidad: number;
//   tieneStock: boolean;
// }

// interface OrderItem {
//   id: number;
//   productoId: number;
//   code: string;
//   description: string;
//   observacion: string;
//   meses: MonthData[];
// }

// const MONTHS = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO"];

/** Enter: siguiente mes en la fila; si es el último, primer mes de la fila siguiente. */
function focusNextMonthQuantityInput(ev: React.KeyboardEvent) {
  const raw = ev.target;
  if (!(raw instanceof HTMLInputElement)) {
    return;
  }
  const row = raw.closest("tr");
  if (!row) {
    return;
  }
  const inputs = Array.from(
    row.querySelectorAll<HTMLInputElement>("input[type='number']"),
  ).filter((el) => !el.disabled);
  const idx = inputs.indexOf(raw);
  if (idx === -1) {
    return;
  }
  if (idx < inputs.length - 1) {
    const next = inputs[idx + 1];
    next.focus();
    next.select();
    return;
  }
  const tbody = row.closest("tbody");
  if (!tbody) {
    return;
  }
  const rows = Array.from(tbody.querySelectorAll("tr"));
  const rowIdx = rows.indexOf(row);
  if (rowIdx < 0 || rowIdx >= rows.length - 1) {
    return;
  }
  const nextRow = rows[rowIdx + 1];
  const first = nextRow.querySelector<HTMLInputElement>(
    "input[type='number']:not(:disabled)",
  );
  if (first) {
    first.focus();
    first.select();
  }
}

/** Misma máscara que «Total importe»: ej. $870.200,00 (`es-AR`, 2 decimales). */
function formatEsArCurrency(amount: number): string {
  return Number(amount).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const PRODUCT_PICKER_PAGE_SIZE = 50;

type OrderRowProps = {
  item: OrderJsonResponse;
  onMonthQuantityChange: (
    productId: string,
    monthIndex: number,
    value: string,
  ) => void;
  onDelete?: (item: OrderJsonResponse) => void;
  showDelete?: boolean;
  isDeleting?: boolean;
  isOrderSent?: boolean;
  discount: number;
  onCheckStock: (item: OrderJsonResponse) => void;
  isCheckingStock: boolean;
  isStockCheckPending: boolean;
  theme: Theme;
};

const OrderRow = React.memo(
  function OrderRow({
    item,
    onMonthQuantityChange,
    onDelete,
    showDelete,
    isDeleting,
    isOrderSent = false,
    theme,
    discount,
    onCheckStock,
    isCheckingStock,
    isStockCheckPending,
  }: OrderRowProps) {
    const rowTotal = useMemo(
      () => item.months.reduce((sum, mes) => sum + (mes.quantity || 0), 0),
      [item],
    );
    const rowImporte = calculateDiscountedTotal(rowTotal, item.price, discount);

    const primaryMain = theme.palette.primary.main;
    const primaryLight = theme.palette.primary.light;

    return (
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
          "&:nth-of-type(even)": {
            backgroundColor: primaryLight + "05",
          },
          "&:hover": {
            backgroundColor: primaryLight + "10",
            transform: "translateY(-1px)",
            boxShadow: `0 2px 8px ${primaryLight}30`,
            transition: "all 0.2s ease-in-out",
          },
          transition: "all 0.2s ease-in-out",
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${primaryLight}20`,
            color: theme.palette.text.primary,
            fontSize: "0.9rem",
            padding: "12px 16px",
          },
        }}
      >
        <TableCell component="th" scope="row">
          {item.code}
        </TableCell>
        <TableCell>${formatEsArCurrency(Number(item.price ?? 0))}</TableCell>
        <TableCell>{item.description}</TableCell>

        {item.months.map((mes, idx) => (
          <TableCell key={idx} align="center">
            <TextField
              size="small"
              type="number"
              value={mes.quantity || ""}
              disabled={!mes.inStock || isOrderSent || isStockCheckPending}
              onChange={(e) =>
                onMonthQuantityChange(item.productId, idx, e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key !== "Enter") {
                  return;
                }
                e.preventDefault();
                focusNextMonthQuantityInput(e);
              }}
              inputProps={{
                min: 0,
                style: { textAlign: "center" },
              }}
              sx={{
                width: 80,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: `1px solid ${primaryMain}`,
                  },
                  backgroundColor: mes.inStock
                    ? "rgba(0, 0, 0, 0.02)"
                    : "rgba(255, 0, 0, 0.08)",
                  borderRadius: 1,
                },
              }}
            />
          </TableCell>
        ))}

        <TableCell align="center">
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{
              color: primaryMain,
              fontSize: "1rem",
            }}
          >
            {rowTotal}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{
              color: primaryMain,
              fontSize: "1rem",
            }}
          >
            ${formatEsArCurrency(rowImporte)}
          </Typography>
        </TableCell>
        {!isOrderSent && (
          <TableCell align="center" padding="checkbox">
            <Tooltip title="Chequear stock">
              <span>
                <IconButton size="small" color="primary" aria-label="Chequear stock"
                  disabled={isStockCheckPending || rowTotal === 0}
                  onClick={() => onCheckStock(item)}>
                  {isCheckingStock ? <CircularProgress size={18} /> : <InventoryIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        )}
        {showDelete && (
          <TableCell align="center" padding="checkbox">
            <IconButton
              size="small"
              color="error"
              aria-label="Eliminar producto del pedido"
              disabled={isDeleting || isStockCheckPending}
              onClick={() => onDelete?.(item)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    );
  },
  (prevProps, nextProps) =>
    prevProps.discount === nextProps.discount &&
    prevProps.onCheckStock === nextProps.onCheckStock &&
    prevProps.isCheckingStock === nextProps.isCheckingStock &&
    prevProps.isStockCheckPending === nextProps.isStockCheckPending &&
    prevProps.item === nextProps.item &&
    prevProps.onMonthQuantityChange === nextProps.onMonthQuantityChange &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.showDelete === nextProps.showDelete &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.isOrderSent === nextProps.isOrderSent &&
    prevProps.theme === nextProps.theme,
);

function mapMonthToApiPayload(m: MonthResponses): {
  month: string;
  year: string;
  count: number;
  monthNumber: number;
} {
  const raw = (m.month || "").trim();
  const count = m.quantity ?? 0;
  const iso = raw.match(/^(\d{4})-(\d{2})/);
  if (iso) {
    return {
      month: iso[2],
      year: iso[1],
      count,
      monthNumber: parseInt(iso[2]),
    };
  }
  const yearMatch = raw.match(/\b(20\d{2})\b/);
  if (yearMatch) {
    const year = yearMatch[1];
    const monthLabel = raw.replace(/\s*\b20\d{2}\b\s*/, "").trim();
    return {
      month: monthLabel || raw,
      year,
      count,
      monthNumber: m.monthNumber,
    };
  }
  return { month: raw, year: "", count, monthNumber: m.monthNumber };
}

function buildOrderCartPayload(
  items: OrderJsonResponse[],
  observation?: string,
  discount = 0,
): OrderCartPayload {
  return {
    observation,
    discount,
    details: items.map((item) => {
      const lineQty = item.months.reduce(
        (s, mes) => s + (mes.quantity || 0),
        0,
      );
      const unitPrice = item.price ?? 0;
      return {
        productId: String(item.productId),
        description: item.description ?? "",
        unitPrice,
        totalPrice: calculateDiscountedTotal(lineQty, unitPrice, discount),
        months: item.months.map((mes) => mapMonthToApiPayload(mes)),
      };
    }),
  };
}

function calculateDiscountedTotal(quantity: number, price: number, discount: number) {
  return Math.round((quantity * price * (1 - discount / 100) + Number.EPSILON) * 100) / 100;
}

const OrderCreatePage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedProductType, setSelectedProductType] =
    useState<ProductType | null>(null);
  const [productTypeDialogOpen, setProductTypeDialogOpen] =
    useState<boolean>(false);
  const [productPickerLoading, setProductPickerLoading] =
    useState<boolean>(false);
  const [pickerProducts, setPickerProducts] = useState<Product[]>([]);
  const [pickerProductsTotal, setPickerProductsTotal] = useState<number>(0);
  const [pickerPage, setPickerPage] = useState<number>(1);
  const [pickerSearchInput, setPickerSearchInput] = useState<string>("");
  const [pickerSearchDebounced, setPickerSearchDebounced] =
    useState<string>("");
  const [tempSelectedProductIds, setTempSelectedProductIds] = useState<
    string[]
  >([]);
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [discountInput, setDiscountInput] = useState("0");
  const [discountRange, setDiscountRange] = useState<{
    minDiscountPercentage: number;
    maxDiscountPercentage: number;
  } | null>(null);
  const [settingsError, setSettingsError] = useState(false);
  const [checkingProductId, setCheckingProductId] = useState<string | null>(null);
  const discount = Number(discountInput);
  const discountValid = discountRange !== null
    && discountInput.trim() !== ""
    && Number.isInteger(discount)
    && discount >= discountRange.minDiscountPercentage
    && discount <= discountRange.maxDiscountPercentage;

  useEffect(() => {
    let cancelled = false;
    orderService.getSettings().then((settings) => {
      if (!cancelled) setDiscountRange(settings);
    }).catch(() => {
      if (!cancelled) setSettingsError(true);
    });
    return () => { cancelled = true; };
  }, []);

  const handleCheckStock = useCallback(async (item: OrderJsonResponse) => {
    if (!orderId) return;
    setCheckingProductId(item.productId);
    try {
      const detail = buildOrderCartPayload([item]).details[0];
      const result = await orderService.checkStock(orderId, detail);
      if (result.hasStock) {
        showSnackbar(`Stock disponible para ${item.description}`, "success", 3000);
      } else {
        setStockErrors(result.errors);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message ?? "Error al chequear stock", "error", 4000);
    } finally {
      setCheckingProductId(null);
    }
  }, [orderId, showSnackbar]);

  const [stockErrors, setStockErrors] = useState<string[]>([]);
  /** Estado del pedido según GET detalle (p. ej. `SENT` → solo lectura). */
  const [orderDetailStatus, setOrderDetailStatus] = useState<
    string | undefined
  >(undefined);

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ observation: string }>({
    defaultValues: { observation: "" },
  });

  const isOrderSent = useMemo(
    () => (orderDetailStatus ?? "").trim().toUpperCase() === "SENT",
    [orderDetailStatus],
  );

  const isOrderDraft = useMemo(
    () => (orderDetailStatus ?? "").trim().toUpperCase() === "DRAFT",
    [orderDetailStatus],
  );

  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null,
  );

  type OrderState = {
    orderItems: OrderJsonResponse[];
    totalCantidad: number;
    totalImporte: number;
  };

  type OrderAction =
    | { type: "SET_FORM"; items: OrderJsonResponse[] }
    | { type: "APPEND_ITEMS"; items: OrderJsonResponse[] }
    | {
        type: "UPDATE_MONTH";
        productId: string;
        monthIndex: number;
        newCount: number;
      }
    | {
        type: "UPDATE_OBSERVATION";
        productId: string;
        newObservation: string;
      };

  const orderItemsIndexRef = useRef<Map<string, number>>(new Map());
  /** Evita re-ejecutar getDetails si solo cambió el contexto (p. ej. tras showSnackbar). */
  const loadedOrderDetailsIdRef = useRef<string | null>(null);

  const computeTotals = useCallback((items: OrderJsonResponse[]) => {
    const totalCantidad = items.reduce((sum, item) => {
      const totalItemQty = item.months.reduce(
        (acc, m) => acc + (m.quantity || 0),
        0,
      );
      return sum + totalItemQty;
    }, 0);

    const totalImporte = items.reduce((sum, item) => {
      const totalItemQty = item.months.reduce(
        (acc, m) => acc + (m.quantity || 0),
        0,
      );
      return sum + totalItemQty * item.price;
    }, 0);

    return { totalCantidad, totalImporte };
  }, []);

  const orderReducer = useCallback(
    (state: OrderState, action: OrderAction): OrderState => {
      switch (action.type) {
        case "SET_FORM": {
          const { totalCantidad, totalImporte } = computeTotals(action.items);
          return {
            orderItems: action.items,
            totalCantidad,
            totalImporte,
          };
        }

        case "APPEND_ITEMS": {
          const mergedItems = [...state.orderItems, ...action.items];
          const { totalCantidad, totalImporte } = computeTotals(mergedItems);
          return {
            orderItems: mergedItems,
            totalCantidad,
            totalImporte,
          };
        }

        case "UPDATE_MONTH": {
          if (!state.orderItems.length) return state;
          const idx = orderItemsIndexRef.current.get(action.productId);
          if (idx == null) return state;

          const currentItem = state.orderItems[idx];
          const month = currentItem.months[action.monthIndex];
          if (!month) return state;

          const oldCount = month.quantity || 0;
          if (oldCount === action.newCount) return state;

          const nextItems = state.orderItems.slice();
          nextItems[idx] = {
            ...currentItem,
            months: currentItem.months.map((mes, mi) =>
              mi === action.monthIndex
                ? { ...mes, quantity: action.newCount }
                : mes,
            ),
          };

          const delta = action.newCount - oldCount;
          return {
            ...state,
            orderItems: nextItems,
            totalCantidad: state.totalCantidad + delta,
            totalImporte: state.totalImporte + delta * currentItem.price,
          };
        }

        case "UPDATE_OBSERVATION": {
          if (!state.orderItems.length) return state;
          const idx = orderItemsIndexRef.current.get(action.productId);
          if (idx == null) return state;

          const currentItem = state.orderItems[idx];
          if (currentItem.observation === action.newObservation) return state;

          const nextItems = state.orderItems.slice();
          nextItems[idx] = {
            ...currentItem,
            observation: action.newObservation,
          };
          return { ...state, orderItems: nextItems };
        }

        default:
          return state;
      }
    },
    [computeTotals],
  );

  const [orderState, dispatch] = useReducer(orderReducer, {
    orderItems: [],
    totalCantidad: 0,
    totalImporte: 0,
  });

  useEffect(() => {
    orderItemsIndexRef.current = new Map(
      orderState.orderItems.map((item, idx) => [String(item.productId), idx]),
    );
  }, [orderState.orderItems]);

  useEffect(() => {
    const oid = orderId?.trim();
    if (!oid) {
      loadedOrderDetailsIdRef.current = null;
      setOrderDetailStatus(undefined);
      return;
    }
    if (loadedOrderDetailsIdRef.current === oid) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const response = await orderService.getOrderDetails(oid);
        if (cancelled) {
          return;
        }
        loadedOrderDetailsIdRef.current = oid;
        setOrderDetailStatus(response.status);
        setValue("observation", response.observations ?? "");
        setDiscountInput(String(response.discount ?? 0));
        const lines = response.formLines;
        if (lines && lines.length > 0) {
          orderItemsIndexRef.current = new Map(
            lines.map((item, idx) => [String(item.productId), idx]),
          );
          dispatch({ type: "SET_FORM", items: lines });
          setMonthStr(lines[0]?.months ?? []);
          setPage(0);
          const row = lines[0];
          if (row) {
            setSelectedProductType({
              productTypeID: row.type ?? "",
              name: `${row.code} — ${row.description}`.slice(0, 120),
              description: undefined,
            });
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          loadedOrderDetailsIdRef.current = null;
          setOrderDetailStatus(undefined);
          showSnackbar(
            error?.response?.data?.message ?? "Error al cargar el pedido",
            "error",
            4000,
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo al montar o si cambia orderId en la URL — no al guardar productos ni showSnackbar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const pagedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return orderState.orderItems.slice(start, start + rowsPerPage);
  }, [orderState.orderItems, page, rowsPerPage]);

  /** IDs de productos ya cargados en NUEVO PEDIDO — no deben aparecer en el picker. */
  const orderProductIds = useMemo(
    () =>
      new Set(
        orderState.orderItems.map((item) => String(item.productId).trim()),
      ),
    [orderState.orderItems],
  );

  const pickerProductsAvailable = useMemo(
    () =>
      pickerProducts.filter(
        (product) => !orderProductIds.has(String(product.productID).trim()),
      ),
    [pickerProducts, orderProductIds],
  );

  const pickerProductsTotalAvailable = useMemo(
    () => Math.max(0, pickerProductsTotal - orderProductIds.size),
    [pickerProductsTotal, orderProductIds],
  );

  const [monthStr, setMonthStr] = useState<MonthResponses[]>();

  useEffect(() => {
    const t = setTimeout(() => {
      setPickerSearchDebounced(pickerSearchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [pickerSearchInput]);

  useEffect(() => {
    if (!productTypeDialogOpen) return;
    setPickerPage(1);
  }, [pickerSearchDebounced, productTypeDialogOpen]);

  useEffect(() => {
    if (!productTypeDialogOpen) return;
    let cancelled = false;
    (async () => {
      const company = JSON.parse(
        localStorage.getItem("company") || "{}",
      ) as CompanyClientResponse;
      const clientId = company.clientID;
      if (!clientId) {
        if (!cancelled) {
          setPickerProducts([]);
          setPickerProductsTotal(0);
          setProductPickerLoading(false);
        }
        return;
      }

      setProductPickerLoading(true);
      try {
        const res = await productService.getProductsByClientIdAsync(
          pickerPage - 1,
          PRODUCT_PICKER_PAGE_SIZE,
          pickerSearchDebounced,
          clientId,
        );
        if (cancelled) return;
        setPickerProducts(res?.products ?? []);
        setPickerProductsTotal(res?.total ?? 0);
      } catch (error: any) {
        if (!cancelled) {
          showSnackbar(
            error.response?.data?.message ?? "Error al cargar productos",
            "error",
            4000,
          );
          setPickerProducts([]);
          setPickerProductsTotal(0);
        }
      } finally {
        if (!cancelled) {
          setProductPickerLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productTypeDialogOpen, pickerPage, pickerSearchDebounced, showSnackbar]);

  useEffect(() => {
    const onCompanyChanged = () => {
      setIsLoading(false);
      setProductPickerLoading(false);
      orderItemsIndexRef.current = new Map();
      loadedOrderDetailsIdRef.current = null;
      dispatch({ type: "SET_FORM", items: [] });
      setSelectedProductType(null);
      setTempSelectedProductIds([]);
      setPickerProducts([]);
      setPickerProductsTotal(0);
      setPickerPage(1);
      setPickerSearchInput("");
      setPickerSearchDebounced("");
      setProductTypeDialogOpen(false);
      setMonthStr(undefined);
      setPage(0);
      setRowsPerPage(25);
      setOrderDetailStatus(undefined);
      setDiscountInput("0");
      setStockErrors([]);
    };
    window.addEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
    return () =>
      window.removeEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
  }, [dispatch]);

  const handleMonthQuantityChange = useCallback(
    (productId: string, monthIndex: number, value: string) => {
      const parsed = parseInt(value, 10);
      const newCount = Number.isNaN(parsed) ? 0 : parsed;
      dispatch({
        type: "UPDATE_MONTH",
        productId,
        monthIndex,
        newCount,
      });
    },
    [],
  );

  const handleDeleteDetail = useCallback(
    async (item: OrderJsonResponse) => {
      if (!isOrderDraft) {
        return;
      }
      const oid = orderId?.trim();
      const detailId = item.orderDetailId?.trim();
      if (!oid) {
        showSnackbar(
          "Falta el identificador del pedido en la URL (?orderId=…)",
          "warning",
          4000,
        );
        return;
      }
      if (!detailId) {
        showSnackbar(
          "No se puede eliminar esta línea: falta el identificador del detalle",
          "warning",
          4000,
        );
        return;
      }

      try {
        setDeletingProductId(item.productId);
        await orderService.deleteOrderDetail(oid, detailId);
        const nextItems = orderState.orderItems.filter(
          (row) => row.productId !== item.productId,
        );
        orderItemsIndexRef.current = new Map(
          nextItems.map((row, idx) => [String(row.productId), idx]),
        );
        dispatch({ type: "SET_FORM", items: nextItems });
        if (nextItems.length === 0) {
          setMonthStr(undefined);
          setSelectedProductType(null);
        }
        showSnackbar("Producto eliminado del pedido", "success", 3000);
      } catch (error: any) {
        showSnackbar(
          error?.response?.data?.message ??
            "Error al eliminar el producto del pedido",
          "error",
          4000,
        );
      } finally {
        setDeletingProductId(null);
      }
    },
    [isOrderDraft, orderId, orderState.orderItems, dispatch, showSnackbar],
  );

  // Actualizar observación general del item
  const handleObservacionChange = useCallback(
    (productId: string, value: string) => {
      dispatch({
        type: "UPDATE_OBSERVATION",
        productId,
        newObservation: value,
      });
    },
    [],
  );

  // Cargar pedido (enviar a backend)
  const handleCargarPedido = async () => {
    try {
      setIsLoading(true);

      // Validar que al menos un producto tenga cantidad
      const hasQuantity = orderState.orderItems.some((item) =>
        item.months.some((mes) => (mes.quantity || 0) > 0),
      );

      if (!hasQuantity) {
        showSnackbar(
          "Debe ingresar al menos una cantidad en algún producto",
          "warning",
          4000,
        );
        setIsLoading(false);
        return;
      }

      // Construir body para enviar
      const orderData = {
        orderId: `ORD-${Date.now()}`,
        status: 1,
        items: orderState.orderItems.map((item) => ({
          productoId: item.productId,
          description: item.description,
          observacion: item.observation,
          meses: item.months,
        })),
      };

      // Aquí iría la llamada al servicio
      // await orderService.createOrder(orderData);

      showSnackbar("Pedido cargado correctamente", "success", 3000);
      navigate("/orders");
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al cargar pedido",
        "error",
        4000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuardarBorrador = async () => {
    const oid = orderId?.trim();
    if (!oid) {
      showSnackbar(
        "Falta el identificador del pedido en la URL (?orderId=…)",
        "warning",
        4000,
      );
      return;
    }
    if (orderState.orderItems.length === 0) {
      showSnackbar(
        "No hay líneas de pedido cargadas. Seleccione productos y continúe.",
        "warning",
        4000,
      );
      return;
    }
    const hasQuantity = orderState.orderItems.some((item) =>
      item.months.some((mes) => (mes.quantity || 0) > 0),
    );
    if (!hasQuantity) {
      showSnackbar(
        "Debe ingresar al menos una cantidad en algún producto",
        "warning",
        4000,
      );
      return;
    }

    if (!discountValid) {
      showSnackbar("Ingrese un descuento comercial entero dentro del rango permitido", "warning", 4000);
      return;
    }
    const isObservationValid = await trigger("observation");
    if (!isObservationValid) {
      showSnackbar(
        "Debe ingresar una observación válida (mínimo 10 caracteres)",
        "warning",
        4000,
      );
      return;
    }

    try {
      setIsLoading(true);
      const payload = buildOrderCartPayload(
        orderState.orderItems,
        getValues("observation"),
        discount,
      );
      await orderService.postOrder(oid, payload);
      showSnackbar("Borrador guardado correctamente", "success", 3000);
      navigate("/orders");
    } catch (error: any) {
      const message: string = error.response?.data?.message ?? "";
      const isStockError =
        message.includes("pedido") && message.includes("disponible");

      if (isStockError) {
        const lines = message
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        setStockErrors(lines);
      } else {
        showSnackbar(message || "Error al guardar el borrador", "error", 4000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnviarPedido = async () => {
    const oid = orderId?.trim();
    if (!oid) {
      showSnackbar(
        "Falta el identificador del pedido en la URL (?orderId=…)",
        "warning",
        4000,
      );
      return;
    }
    if (orderState.orderItems.length === 0) {
      showSnackbar(
        "No hay líneas de pedido cargadas. Seleccione productos y continúe.",
        "warning",
        4000,
      );
      return;
    }
    const hasQuantity = orderState.orderItems.some((item) =>
      item.months.some((mes) => (mes.quantity || 0) > 0),
    );
    if (!hasQuantity) {
      showSnackbar(
        "Debe ingresar al menos una cantidad en algún producto",
        "warning",
        4000,
      );
      return;
    }

    if (!discountValid) {
      showSnackbar("Ingrese un descuento comercial entero dentro del rango permitido", "warning", 4000);
      return;
    }
    const isObservationValid = await trigger("observation");
    if (!isObservationValid) {
      showSnackbar(
        "Debe ingresar una observación válida (mínimo 10 caracteres)",
        "warning",
        4000,
      );
      return;
    }

    try {
      setIsLoading(true);
      const payload = buildOrderCartPayload(
        orderState.orderItems,
        getValues("observation"),
        discount,
      );
      await orderService.postOrder(oid, payload);
      await orderService.sendOrder(oid);
      showSnackbar("Pedido enviado correctamente", "success", 3000);
      navigate("/orders");
    } catch (error: any) {
      const message: string = error.response?.data?.message ?? "";
      const isStockError =
        message.includes("pedido") && message.includes("disponible");

      if (isStockError) {
        const lines = message
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        setStockErrors(lines);
      } else {
        showSnackbar(message || "Error al enviar el pedido", "error", 4000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const totalCantidad = orderState.totalCantidad;
  const appliedDiscount = discountValid ? discount : 0;
  const totalImporte = orderState.orderItems.reduce((sum, item) => {
    const quantity = item.months.reduce((qty, month) => qty + (month.quantity || 0), 0);
    return sum + calculateDiscountedTotal(quantity, item.price, appliedDiscount);
  }, 0);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseInt(event.target.value, 10);
      setRowsPerPage(next);
      setPage(0);
    },
    [],
  );

  const productPickerColumns = useMemo(
    () => [
      {
        header: "",
        key: "select",
        disableSort: true,
        render: (row: Product) => (
          <Checkbox
            size="small"
            checked={tempSelectedProductIds.includes(row.productID)}
            onChange={(_, checked) => {
              setTempSelectedProductIds((prev) => {
                if (checked) {
                  return prev.includes(row.productID)
                    ? prev
                    : [...prev, row.productID];
                }
                return prev.filter((id) => id !== row.productID);
              });
            }}
            inputProps={{
              "aria-label": `Seleccionar producto ${row.code}`,
            }}
          />
        ),
      },
      { header: "Código", key: "code" },
      { header: "Descripción", key: "description" },
      { header: "Tipo producto", key: "productType" },
      { header: "Precio unitario", key: "priceStr" },
    ],
    [tempSelectedProductIds],
  );

  const handleProductPickerPageChange = useCallback((nextPage: number) => {
    setPickerPage(nextPage);
  }, []);

  const openProductTypeDialog = () => {
    setProductTypeDialogOpen(true);
    setTempSelectedProductIds([]);
    setPickerPage(1);
    setPickerSearchInput("");
    setPickerSearchDebounced("");
  };

  const handleSeleccionarProductTypeClick = () => {
    openProductTypeDialog();
  };

  const handleCloseProductTypeDialog = () => {
    setProductTypeDialogOpen(false);
    setTempSelectedProductIds([]);
    setPickerSearchInput("");
    setPickerSearchDebounced("");
    setPickerPage(1);
  };

  const handleConfirmProductType = async () => {
    if (tempSelectedProductIds.length === 0) {
      showSnackbar(
        "Seleccione al menos un producto de la lista",
        "warning",
        3000,
      );
      return;
    }
    const oid = orderId?.trim();
    if (!oid) {
      showSnackbar(
        "Falta el identificador del pedido en la URL (?orderId=…)",
        "warning",
        4000,
      );
      return;
    }
    const company = JSON.parse(
      localStorage.getItem("company") || "{}",
    ) as CompanyClientResponse;
    if (!company.clientID) {
      showSnackbar(
        "No hay empresa (cliente) en sesión. Elija empresa en el menú.",
        "warning",
        4000,
      );
      return;
    }

    const idsSnapshot = [...tempSelectedProductIds];

    try {
      const items = await orderService.saveOrderProducts({
        orderId: oid,
        clientId: company.clientID,
        productIds: idsSnapshot,
      });

      handleCloseProductTypeDialog();

      const hadItems = orderState.orderItems.length > 0;
      const mergedCount = orderState.orderItems.length + items.length;

      dispatch({ type: "APPEND_ITEMS", items });

      if (!monthStr?.length && items[0]?.months?.length) {
        setMonthStr(items[0].months);
      }

      setPage(Math.max(0, Math.ceil(mergedCount / rowsPerPage) - 1));

      if (!hadItems && items.length > 0) {
        const row = items[0];
        setSelectedProductType({
          productTypeID: row.type ?? "",
          name: `${row.code} — ${row.description}`.slice(0, 120),
          description: undefined,
        });
      }

      showSnackbar("Productos guardados en el pedido", "success", 3000);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message ??
          "Error al guardar productos en el pedido",
        "error",
        4000,
      );
    }
  };

  const handleBackToCart = () => {
    navigate("/orders");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <IconButton
          color="primary"
          aria-label="Volver al carrito"
          onClick={handleBackToCart}
          sx={{
            border: `1px solid ${theme.palette.primary.light}60`,
            borderRadius: 2,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Card
          sx={{
            flex: "1 1 240px",
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0, 119, 194, 0.08)",
          }}
        >
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Total unidades
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {totalCantidad}
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: "1 1 240px",
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0, 119, 194, 0.08)",
          }}
        >
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Total importe
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              ${formatEsArCurrency(totalImporte)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 3 }}>
        {/* <Typography variant="body2" color="text.secondary" gutterBottom>
          Tipo de producto seleccionado:
        </Typography> */}
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          {/* <Typography
            variant="body1"
            fontWeight={600}
            color="primary.main"
            sx={{ minWidth: 120 }}
          >
            {selectedProductType?.name ?? "—"}
          </Typography> */}
          {!isOrderSent && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleSeleccionarProductTypeClick}
            >
              Agregar producto
            </Button>
          )}
        </Stack>
      </Box>

      <Card>
        <CardContent>
          {/* Título */}
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ mb: 3, color: "primary.main", fontWeight: 600 }}
          >
            NUEVO PEDIDO
          </Typography>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : selectedProductType &&
            orderState.orderItems.length === 0 &&
            (monthStr?.length ?? 0) > 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No hay stock del tipo de producto seleccionado
            </Alert>
          ) : (
            <>
              {/* Tabla de pedidos */}
              <TableContainer
                component={Paper}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0, 119, 194, 0.1)",
                  border: `1px solid ${theme.palette.primary.light}20`,
                  overflow: "auto",
                  maxWidth: "100%",
                }}
              >
                <Table sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        "& .MuiTableCell-root": {
                          borderBottom: `2px solid ${theme.palette.primary.dark}`,
                          color: theme.palette.background.paper,
                          fontWeight: "bold",
                          fontSize: "0.95rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          padding: "16px",
                        },
                      }}
                    >
                      <TableCell>Cod Produc</TableCell>
                      <TableCell>Precio unitario sin descuento</TableCell>
                      <TableCell>Descripcion</TableCell>
                      {monthStr?.map(
                        (month) =>
                          month && (
                            <TableCell key={month.month} align="center">
                              {month.month}
                            </TableCell>
                          ),
                      )}
                      <TableCell align="center">TOTAL</TableCell>
                      <TableCell align="center">Precio total con descuento</TableCell>
                      {!isOrderSent && <TableCell align="center">Stock</TableCell>}
                      {isOrderDraft && (
                        <TableCell align="center" padding="checkbox" />
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedItems.map((item) => (
                      <OrderRow
                        key={item.productId}
                        item={item}
                        discount={appliedDiscount}
                        onCheckStock={handleCheckStock}
                        isCheckingStock={checkingProductId === item.productId}
                        isStockCheckPending={checkingProductId !== null || isLoading}
                        onMonthQuantityChange={handleMonthQuantityChange}
                        onDelete={handleDeleteDetail}
                        showDelete={isOrderDraft}
                        isDeleting={deletingProductId === item.productId}
                        isOrderSent={isOrderSent}
                        theme={theme}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={orderState.orderItems.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Filas"
              />
            </>
          )}

          <Box sx={{ my: 2 }}>
            <TextField label="Descuento comercial (%)" type="number" size="small"
              value={discountInput} onChange={(e) => setDiscountInput(e.target.value)}
              disabled={isOrderSent || isLoading || !discountRange}
              inputProps={{ min: discountRange?.minDiscountPercentage, max: discountRange?.maxDiscountPercentage, step: 1 }}
              error={settingsError || (!!discountRange && !discountValid)}
              helperText={settingsError ? "No se pudo cargar el rango de descuento. Recargue la pantalla."
                : discountRange ? `Ingrese un entero entre ${discountRange.minDiscountPercentage}% y ${discountRange.maxDiscountPercentage}%`
                : "Cargando rango permitido..."} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Importe sin descuento: ${formatEsArCurrency(orderState.totalImporte)} · Descuento: ${formatEsArCurrency(orderState.totalImporte - totalImporte)}
            </Typography>
          </Box>
          {/* Botones (ocultos si el pedido ya fue enviado) */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="observation"
              control={control}
              rules={{
                // required: "La observación es requerida",
                // minLength: {
                //   value: 10,
                //   message: "La observación debe tener al menos 10 caracteres",
                // },
                maxLength: {
                  value: 120,
                  message: "La observación no puede exceder los 120 caracteres",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observación"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.observation}
                  helperText={errors.observation?.message}
                  placeholder="Ingrese observaciones sobre el stock..."
                  disabled={isOrderSent}
                />
              )}
            />
          </Box>
          {!isOrderSent && (
            <>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={handleGuardarBorrador}
                  disabled={isLoading || checkingProductId !== null || !discountValid}
                >
                  Guardar borrador
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleEnviarPedido}
                  disabled={isLoading || checkingProductId !== null || !discountValid}
                >
                  Enviar pedido
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={productTypeDialogOpen}
        onClose={handleCloseProductTypeDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 119, 194, 0.12)",
          },
        }}
      >
        <DialogTitle sx={{ color: "primary.main", fontWeight: 600 }}>
          Seleccionar producto
        </DialogTitle>
        <DialogContent sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2} sx={{ pt: 1, width: "100%" }}>
            <TextField
              size="small"
              fullWidth
              label="Buscar"
              placeholder="Código o descripción (mismo criterio que el listado de productos)"
              value={pickerSearchInput}
              onChange={(e) => setPickerSearchInput(e.target.value)}
            />
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <DataTable
                columns={productPickerColumns}
                data={pickerProductsAvailable}
                page={pickerPage}
                total={pickerProductsTotalAvailable}
                isLoading={productPickerLoading}
                onChange={handleProductPickerPageChange}
                rowsPerPage={PRODUCT_PICKER_PAGE_SIZE}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Productos del cliente en sesión. Puede marcar varios; al continuar
              se guardan en el pedido (máx. {PRODUCT_PICKER_PAGE_SIZE} por
              página).
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseProductTypeDialog} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmProductType}
            variant="contained"
            color="primary"
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <StockErrorDialog
        open={stockErrors.length > 0}
        errors={stockErrors}
        onClose={() => setStockErrors([])}
      />
    </Box>
  );
};

export default OrderCreatePage;
